import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useMemo } from 'react';
import { isSameDay, startOfMonth } from 'date-fns';
import { Spinner } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { HistoricAgreementStatsModel } from '~/models';
import { RelationshipProgressStatus } from '~/models/relationship';
import { AgreementStatus } from '~/models/relationship-summary';
import { selectors } from '~/store/state-selectors';
import { getLastXMonths } from '~/utils/date.utils';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import ThreeLineTopCard from '../GraphWidget/ThreeLineTopCard';
import styles from './HistoricAgreementStatsWidget.module.scss';
import { historicAgreementStatsWidgetTextsPath } from './HistoricAgreementStatsWidget.utils';
import { useHistoricAgreementStats } from './hooks/useHistoricAgreementStats';
export { historicAgreementStatsWidgetTextsPath };
export const GetHistoricAgreementStatsWidgetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            HistoricAgreementStatsWidgetTID: {
                Error: 'widget-error',
                AgreementsSummary: 'agreements-summary',
                ProposalsSummary: 'proposals-summary',
                ChartPlaceholder: 'agreement-stats-chart-placeholder',
                Stats: 'agreement-stats',
                Chart: 'agreement-stats-chart'
            }
        }
    });
const HistoricAgreementStatsChartInner = /*#__PURE__*/ lazy(()=>import(/* webpackChunkName: "recharts" */ './HistoricAgreementStatsChartInner').then((module)=>({
            default: module.HistoricAgreementStatsChartInner
        })));
const getCard1Props = (texts, testId)=>({
        testId,
        title: texts.activeAgreements(),
        success: true,
        titleInfo: texts.activeAgreementsTooltip(),
        secondaryInfo: texts.activeAgreementsSecondaryTooltip()
    });
const getCard2Props = (texts, testId)=>({
        testId,
        title: texts.awaitingApproval(),
        titleInfo: texts.awaitingApprovalTooltip(),
        secondaryInfo: texts.awaitingApprovalSecondaryTooltip()
    });
export function fillLast12Months(data, now) {
    return getLastXMonths(now, 12).map((date)=>data.find((item)=>isSameDay(startOfMonth(date), startOfMonth(item.date()))) || HistoricAgreementStatsModel.makeEmpty({
            date
        }));
}
function HistoricAgreementStatsChart(param) {
    let { testId, data, isTestMode } = param;
    const { getNow } = useIoContext();
    const fullData = useMemo(()=>fillLast12Months(data, getNow()), [
        data
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(Spinner, {
                size: 30
            }),
            children: /*#__PURE__*/ _jsx(HistoricAgreementStatsChartInner, {
                testId: testId,
                data: fullData,
                isTestMode: isTestMode
            })
        })
    });
}
function PlaceholderChart(props) {
    const { getNow } = useIoContext();
    const { HistoricAgreementStatsWidgetTID } = useMemo(()=>GetHistoricAgreementStatsWidgetTID(), []);
    const placeholderData = useMemo(()=>{
        const emptyData = fillLast12Months([], getNow());
        return emptyData.map((item, idx)=>item.setActiveAgreementsCount(Math.floor(idx / 2) + 3).setProposalsCount(3));
    }, []);
    return /*#__PURE__*/ _jsx(HistoricAgreementStatsChart, _object_spread({
        testId: HistoricAgreementStatsWidgetTID.ChartPlaceholder(),
        data: placeholderData
    }, props));
}
const Layout = (param)=>{
    let { loading, children } = param;
    const { t: historicAgreementStatsWidgetTexts } = useLocaleKeys('home.dashboard.historicAgreementStatsWidget');
    return /*#__PURE__*/ _jsx(GraphWidgetLayout, {
        title: historicAgreementStatsWidgetTexts.title(),
        loading: loading,
        bottomHeight: "l",
        children: children
    });
};
export default function HistoricAgreementStatsWidget(props) {
    const { t: widgetTexts } = useLocaleKeys(historicAgreementStatsWidgetTextsPath);
    const { HistoricAgreementStatsWidgetTID } = useMemo(()=>GetHistoricAgreementStatsWidgetTID(), []);
    const card1Props = useMemo(()=>getCard1Props(widgetTexts, HistoricAgreementStatsWidgetTID.AgreementsSummary()), [
        HistoricAgreementStatsWidgetTID
    ]);
    const card2Props = useMemo(()=>getCard2Props(widgetTexts, HistoricAgreementStatsWidgetTID.ProposalsSummary()), [
        HistoricAgreementStatsWidgetTID
    ]);
    const { historicAgreementStats, isLoading, isFetching, error } = useHistoricAgreementStats({
        refreshOnHookMount: true
    });
    const hasSentAProposal = useStore(selectors.hasSentAProposal);
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const loadingOrEmptyBg = ()=>/*#__PURE__*/ _jsx(PlaceholderChart, _object_spread({}, props));
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: HistoricAgreementStatsWidgetTID.Error()
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(Layout, {
            loading: true,
            children: [
                /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card1Props), {
                    loading: true
                })),
                /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card2Props), {
                    loading: true
                })),
                /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                    loading: true,
                    loadingMsg: widgetTexts.loadingMsg(),
                    loadingOrEmptyBg: loadingOrEmptyBg
                })
            ]
        });
    }
    const isEmpty = historicAgreementStats.length === 0;
    const firstItem = isEmpty ? HistoricAgreementStatsModel.makeEmpty() : historicAgreementStats[0];
    return /*#__PURE__*/ _jsxs(Layout, {
        loading: isFetching,
        children: [
            /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card1Props), {
                primaryText: firstItem.agreementsCount(),
                secondaryText: isMemberUser ? undefined : widgetTexts.monthlyRecurring({
                    amount: firstItem.agreementsDollarAmount()
                }),
                linkTo: "".concat(routes.agreementsDashboard, "?agreementStatus=").concat(AgreementStatus.Active)
            })),
            /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card2Props), {
                primaryText: firstItem.proposalsCount(),
                secondaryText: isMemberUser ? undefined : widgetTexts.monthlyRecurring({
                    amount: firstItem.proposalsDollarAmount()
                }),
                linkTo: "".concat(routes.proposalsDashboard, "?filter=").concat(RelationshipProgressStatus.PaymentMethod, ",").concat(RelationshipProgressStatus.Reviewed, ",").concat(RelationshipProgressStatus.Sent)
            })),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                empty: isEmpty && !hasSentAProposal,
                emptyMsg: widgetTexts.emptyMsg(),
                testId: HistoricAgreementStatsWidgetTID.Stats(),
                loadingOrEmptyBg: loadingOrEmptyBg,
                children: /*#__PURE__*/ _jsx(HistoricAgreementStatsChart, _object_spread({
                    testId: HistoricAgreementStatsWidgetTID.Chart(),
                    data: historicAgreementStats
                }, props))
            })
        ]
    });
}
